import type { Node } from '@xyflow/react'
import { getParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'
import type { QueryFilterGroup } from '@/services/api/scanner-types'

export interface UniverseNodeState {
  universeName: string
  filters: QueryFilterGroup | null
  /** Cached result of the last resolve. */
  inputCount: number
  /** When that resolve happened. Its PRESENCE is what separates "resolved to
   *  nothing" from "never resolved"; a count of zero is a real answer. */
  resolvedAt: string
}

const EMPTY: UniverseNodeState = { universeName: '', filters: null, inputCount: 0, resolvedAt: '' }

/**
 * Read a universe node's configuration off the graph.
 *
 * The filter tree is persisted as JSON in a text parameter (the `csvData`
 * pattern), so reading it is a parse, and a parse of user-reachable state has
 * to be total. A node whose `filters` is malformed opens the sheet empty rather
 * than throwing inside the editor's render, because losing the filters is
 * recoverable and a white screen is not.
 */
export function readUniverseValue(
  nodes: Node[] | undefined,
  nodeId: string | null,
): UniverseNodeState {
  if (!nodes || !nodeId) return EMPTY
  const node = nodes.find((n) => n.id === nodeId)
  if (!node) return EMPTY
  return readUniverseParams((node.data as Record<string, unknown> | undefined)?.parameters)
}

/**
 * The same read, from a node's `parameters` directly. The node body's
 * Conditions section has the data in hand and no graph to search.
 *
 * Parameters live in TWO shapes on this canvas: a `{ name, value }[]` list
 * (what the API wants) and a `{ [name]: value }` record (what the palette
 * writes on creation). `getParameterValue` reads both; an earlier version of
 * this function only accepted the list, so a freshly placed universe node
 * kept saying "No conditions yet" after Apply even though its parameters had
 * changed underneath.
 */
export function readUniverseParams(params: unknown): UniverseNodeState {
  if (!params || typeof params !== 'object') return EMPTY
  const universeName = String(getParameterValue(params, 'universeName') ?? '')
  const raw = getParameterValue(params, 'filters')
  const rawCount = getParameterValue(params, 'inputCount')
  const parsedCount = typeof rawCount === 'number' ? rawCount : Number(rawCount ?? 0)

  return {
    universeName,
    filters: parseFilters(raw),
    inputCount: Number.isFinite(parsedCount) ? parsedCount : 0,
    resolvedAt: String(getParameterValue(params, 'resolvedAt') ?? ''),
  }
}

function parseFilters(raw: unknown): QueryFilterGroup | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  try {
    const parsed: unknown = JSON.parse(raw)
    // A filter group is an object. Anything else (a bare string, an array, a
    // number) came from somewhere that did not write a filter group, and
    // handing it to the form would break the form rather than this function.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed as QueryFilterGroup
  } catch {
    return null
  }
}
